import { Chip } from "@dropbox/dig-components/chip";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { getSourceRowParts } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import { openURL } from "@mirage/service-platform-actions";
import styles from "./MarkdownCitationChip.module.css";

import { Overlay } from "@dropbox/dig-components/overlay";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Click_SourceFile } from "@mirage/analytics/events/types/click_source_file";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useDelayedHover } from "@mirage/mosaics/ConversationAssistant/hooks/useDelayedHover";
import { AnswerCitationCard } from "@mirage/mosaics/MultiAnswersCard/AnswerCitationCard";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import React, { memo, useRef } from "react";

interface MarkdownCitationChipProps {
  source: ChatSource;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
}
export const MarkdownCitationChip = memo(
  ({
    source,
    logComposeEvent,
    conversationId,
    searchSessionId,
    assistRequestId,
  }: MarkdownCitationChipProps) => {
    const {
      isHovered,
      handlers: { onMouseEnter, onMouseLeave },
    } = useDelayedHover();
    const { searchQueryUuid } = useSearchQueryId();
    const anchorRef = useRef(null);
    const parts = getSourceRowParts(source);

    if (!parts) {
      return null;
    }

    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      if (parts?.mirageResult?.url && source.type !== "local_file_content") {
        logComposeEvent(
          PAP_Click_SourceFile({
            actionType: "search_result",
            conversationId,
            searchSessionId,
            searchQueryUuid,
            assistRequestId,
            resultUuid: parts.mirageResult.uuid,
          }),
        );
        openURL(parts.mirageResult.url);
      }
    };

    return (
      <div
        className={styles.MarkdownCitationChipContainer}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Chip
          size="small"
          className={styles.MarkdownCitationChip}
          onClick={handleClick}
          ref={anchorRef}
        >
          <Chip.Content className={styles.MarkdownCitationChipContent}>
            <Text
              size="small"
              tagName="div"
              color="subtle"
              className={styles.MarkdownCitationChipText}
            >
              <Truncate>{parts?.title}</Truncate>
            </Text>
          </Chip.Content>
        </Chip>
        {isHovered && (
          <Overlay
            offsetDistance={2}
            anchorRef={anchorRef}
            placement="bottom-start"
            auto={true}
            className={styles.MarkdownCitationOverlayContent}
          >
            <div className={styles.MarkdownCitationSourceCard}>
              <AnswerCitationCard
                source={source}
                config={{
                  time: {
                    timeAgo: {
                      abbreviateTime: true,
                      abbreviateDate: false,
                    },
                  },
                  icon: {
                    hasBackground: false,
                    size: "small",
                  },
                  subtitle: {
                    hideServiceName: true,
                    hideSizeOrMtime: false,
                    fallbackToServiceName: true,
                  },
                  dynamicTitleHeight: true,
                }}
                conversationId={conversationId}
                logComposeEvent={logComposeEvent}
                searchSessionId={searchSessionId}
                assistRequestId={assistRequestId}
              />
            </div>
          </Overlay>
        )}
      </div>
    );
  },
);
MarkdownCitationChip.displayName = "MarkdownCitationChip";
